import { useEffect, useRef } from "react";

interface UseEdgeScrollOptions {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  cameraRef: React.RefObject<{ x: number; y: number; zoom: number }>;
  draw: () => void;
  onScrollPaint: () => void;
  disabled?: boolean;
}

export function useEdgeScroll({
  canvasRef,
  cameraRef,
  draw,
  onScrollPaint,
  disabled = false,
}: UseEdgeScrollOptions) {
  const edgeScrollRef = useRef<{ dx: number; dy: number; frameId: number | null }>({
    dx: 0,
    dy: 0,
    frameId: null,
  });
  const lastMouseRef = useRef<{ clientX: number; clientY: number } | null>(null);

  // Stable refs so the rAF loop always sees latest callbacks
  const drawRef = useRef(draw);
  const onScrollPaintRef = useRef(onScrollPaint);

  useEffect(() => {
    drawRef.current = draw;
    onScrollPaintRef.current = onScrollPaint;
  }, [draw, onScrollPaint]);

  function startLoop() {
    const es = edgeScrollRef.current;
    function tick() {
      const cam = cameraRef.current;
      cam.x += es.dx;
      cam.y += es.dy;

      onScrollPaintRef.current();
      drawRef.current();

      if (es.dx !== 0 || es.dy !== 0) {
        es.frameId = requestAnimationFrame(tick);
      } else {
        es.frameId = null;
      }
    }
    es.frameId = requestAnimationFrame(tick);
  }

  function updateEdgeScroll(clientX: number, clientY: number) {
    if (disabled) {
      stopEdgeScroll();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const EDGE = 40;
    const MAX_SPEED = 8;
    let dx = 0;
    let dy = 0;

    const left = clientX - rect.left;
    const right = rect.right - clientX;
    const top = clientY - rect.top;
    const bottom = rect.bottom - clientY;

    if (left < EDGE) dx = MAX_SPEED * (1 - left / EDGE);
    else if (right < EDGE) dx = -MAX_SPEED * (1 - right / EDGE);
    if (top < EDGE) dy = MAX_SPEED * (1 - top / EDGE);
    else if (bottom < EDGE) dy = -MAX_SPEED * (1 - bottom / EDGE);

    const es = edgeScrollRef.current;
    es.dx = dx;
    es.dy = dy;

    if ((dx !== 0 || dy !== 0) && es.frameId === null) {
      startLoop();
    } else if (dx === 0 && dy === 0 && es.frameId !== null) {
      cancelAnimationFrame(es.frameId);
      es.frameId = null;
    }
  }

  function stopEdgeScroll() {
    const es = edgeScrollRef.current;
    es.dx = 0;
    es.dy = 0;
    if (es.frameId !== null) {
      cancelAnimationFrame(es.frameId);
      es.frameId = null;
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => stopEdgeScroll();
  }, []);

  useEffect(() => {
    if (disabled) {
      stopEdgeScroll();
    }
  }, [disabled]);

  return { updateEdgeScroll, stopEdgeScroll, lastMouseRef };
}
